import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MutableRefObject,
  type RefObject,
} from 'react';
import type { TreeNavigatorRef } from './TreeNavigator';
import { Maximize2, Minimize2 } from 'lucide-react';
import type {
  ModelSchema,
  UiPrefs,
  UiPrefsPatch,
  ZoomLevel,
} from '@arcanum-hq/shared-types';
import { DEFAULT_ZOOM, ZOOM_LEVELS } from '@arcanum-hq/shared-types';
import type { ModelViewResponse } from '@/lib/api';
import { useUiPrefs } from '@/hooks/use-ui-prefs';
import {
  logResolutionFailure,
  resolveDeepLink,
} from '@/lib/deep-link-resolution';
import { CellDetailPanel } from './CellDetailPanel';
import { KeyboardShortcutsOverlay } from './KeyboardShortcutsOverlay';
import { ModelGrid } from './ModelGrid';
import { SheetTabs } from './SheetTabs';
import { TreeNavigator } from './TreeNavigator';
import { findNamedRange } from './cell-detail-helpers';
import { parseA1 } from './grid-helpers';

// `data.schema` from the API is a partial `ModelSchema` shape (minimum
// fields the model-view route ships); `resolveDeepLink` only reads
// `inputs[]` + `outputs[]`. Cast at the call boundary so we don't widen
// the API response type just for this consumer.
type ModelSchemaForDeepLink = ModelSchema;

/**
 * Route-level bridge ref. ADR-0021 Option B (builder-ssot-consolidation S7.3):
 * the `applyDiff` arm has been removed — the bridge is now reset-only.
 * `reset()` reseeds the grid's local `cellValues` + `inputValues` caches from
 * the latest `data` prop (the snapshot/large-gap reseed path that Option B
 * relies on). Small-gap catch-up is handled via invalidateQueries in the
 * channel hook, which triggers a full `/views` refetch.
 */
export interface ModelViewBridge {
  reset?: () => void;
}

interface ModelLayoutProps {
  data: ModelViewResponse;
  /**
   * When true, edit affordances are hidden everywhere downstream. Phase 3
   * has no edit affordances yet — this prop is here so `external-sharing`
   * (future feature) can adopt the layout without changes.
   */
  readOnly?: boolean;
  /** Conversation id — required for input-cell edit submissions. */
  conversationId?: string | null;
  /** Phase 4 Session 3: View-in-Conversation toast callback (parent flips `?tab=conversation`). */
  onViewInConversation?: () => void;
  /**
   * Phase 4 Session 4: route-level bridge ref. The parent (`<BuilderPage>`)
   * declares one `useRef<ModelViewBridge>({})` and passes it down.
   * `<ModelLayout>` registers `reset` into `bridgeRef.current` on mount;
   * the parent's `onSnapshotApplied` callback calls
   * `modelViewBridgeRef.current.reset?.()` to reseed the grid after a
   * snapshot or large-gap reconnect (ADR-0021 Option B).
   */
  bridgeRef?: MutableRefObject<ModelViewBridge>;
  /**
   * Phase 3 Session 3.3 (model-view-ux-polish, 2026-04-27): parent-callback
   * fired whenever the fullscreen state changes. Lets the Builder route hide
   * its sidebar + (in conversation tab) chat column. Optional — without it,
   * the toggle still works visually but the parent layout doesn't react.
   * State is local component state — resets on unmount (route change).
   */
  onFullscreenChange?: (isFullscreen: boolean) => void;
  /**
   * Phase 3 Session 3.4 (model-view-ux-polish, 2026-04-27): initial UI prefs
   * sourced from the parent's `useConversation().uiPrefs`. Seeds the
   * `colWidthsBySheet` local state on mount; subsequent drags update local
   * state and persist via the debounced `useUiPrefs` mutation. Cross-tab
   * visibility is on next page load only (spec §7 — no live mirroring).
   */
  uiPrefs?: UiPrefs;
  /**
   * Phase 3 Session 5 (agent-ux-optimization, 2026-05-07): URL deep-link
   * params + write-back. When set, the layout resolves the param to a
   * concrete cell via `resolveDeepLink` and dispatches the existing
   * `selectAndPulse` flow exactly as a tree-leaf click would. Resolution
   * failure is silent (logged to console + window event); successful
   * resolution invokes `onConsumeDeepLink` so the parent can drop the
   * URL param after the scroll fires (prevents re-trigger on prop change).
   */
  cellParam?: string;
  namedRangeParam?: string;
  searchParam?: string;
  onConsumeDeepLink?: (params: {
    cell?: boolean;
    namedRange?: boolean;
  }) => void;
  onSearchChange?: (next: string | undefined) => void;
  /**
   * Sandbox edit pathway — `external-sharing` Phase 5 Session 5.4
   * (2026-05-09). Optional outbound callback fired when a user commits
   * an input-cell edit via `<InputCellEditor>` (Enter / Save). When
   * set, the edit routes through this callback INSTEAD of the
   * auth-mode `useModelEdit` mutation in `<CellDetailPanel>`. Used by
   * `<DealModelView>` (public deal page) and `<ReadOnlyShareBuilder>`
   * (`/builder?share=<id>`) to thread edits into
   * `useSandboxOverrides.applyEdit`. Auth-mode parents pass undefined
   * to keep the existing commit path active.
   */
  onInputEdit?: (namedRange: string, value: unknown) => void;
}

const TREE_COLLAPSED_KEY = 'arcanum:model-view:tree-collapsed';

function readCollapsedFromStorage(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(TREE_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Three-pane composition for the Model tab:
 *
 *   [ TreeNavigator ] [ ModelGrid + SheetTabs ] [ CellDetailPanel ]
 *
 * Phase 4 Session 1 shipped the real `<TreeNavigator>` with auto-tree from
 * schema + visible sheets, scroll-to-cell wiring (with re-trigger via
 * nonce-keyed prop), 2s pulse animation, and sessionStorage-backed
 * collapsed state.
 *
 * Phase 4 Session 2 ships the real `<CellDetailPanel>` (formula/output
 * branch). The panel's upstream-click flow rises through the same
 * `selectAndPulse` path the tree uses — `onUpstreamSelect` resolves the
 * named range to `{sheet, row, col}` via `data.schema.allNamedRanges` and
 * dispatches the existing `setSelectedCell` + nonce-keyed `setScrollToCell`,
 * so clicking an upstream node walks the dependency chain identically to
 * a tree leaf click.
 *
 * Selection state (`selectedCell`) lives here so both the grid and the cell
 * detail pane can read it; the tree navigator + cell-detail upstream click
 * both drive `setSelectedCell` + the nonce-keyed `setScrollToCell`.
 */
export function ModelLayout({
  data,
  readOnly = false,
  conversationId = null,
  onViewInConversation,
  bridgeRef,
  onFullscreenChange,
  uiPrefs,
  cellParam,
  namedRangeParam,
  searchParam,
  onConsumeDeepLink,
  onSearchChange,
  onInputEdit,
}: ModelLayoutProps) {
  const [activeSheet, setActiveSheet] = useState<string>(
    data.layout.visibleSheetNames[0] ?? ''
  );
  const [selectedCell, setSelectedCell] = useState<{
    row: number;
    col: number;
  } | null>(null);

  // Identity of the tree leaf the user clicked (null for grid-/search-/deep-
  // link-originated selection). Drives the TreeNavigator's active-leaf
  // highlight by unique leaf id so multiple schema entries mapping to the same
  // cell don't all light up. (tree-nav multi-select fix, 2026-06-11.)
  const [selectedLeafId, setSelectedLeafId] = useState<string | null>(null);

  // Phase 2 S3 — closure-staleness mirror per research/phase-2.md § 11 R-G.
  // The grid-root keyboard handler is a long-lived useCallback closure; reading
  // `selectedCell` directly from that closure causes stale reads on rapid arrow
  // nav (200+ keypresses/s). The ref ensures the handler always reads the latest
  // committed value at handler-fire time.
  const selectedCellRef = useRef<{ row: number; col: number } | null>(
    selectedCell
  );
  useEffect(() => {
    selectedCellRef.current = selectedCell;
  }, [selectedCell]);

  // Tree-navigator-driven scroll target. `nonce` bumps on every leaf click
  // so re-clicking the same target re-fires the scroll + pulse via the
  // grid's effect (referential equality).
  const [scrollToCell, setScrollToCell] = useState<{
    row: number;
    col: number;
    nonce: number;
  } | null>(null);
  const nonceRef = useRef(0);

  // Phase 2 S5 — ref for the TreeNavigator's imperative focusSearchInput()
  // handle. Used by the Ctrl+F / Cmd+F handler below (ADR-0008 two-mount
  // design: Ctrl+F mounts on ModelLayout root, NOT on grid-root).
  const treeNavigatorRef = useRef<TreeNavigatorRef | null>(null);

  // Locally-mutable cellValues + inputValues caches seeded from the prop.
  // When `data` itself changes (a model-view refetch — typically a snapshot
  // reset path), we reseed from props.
  const [cellValues, setCellValues] = useState<Record<string, unknown>>(
    data.cellValues
  );
  const [inputValues, setInputValues] = useState<Record<string, unknown>>(
    data.inputValues
  );
  useEffect(() => {
    setCellValues(data.cellValues);
    setInputValues(data.inputValues);
  }, [data.cellValues, data.inputValues]);

  // Register bridge handlers on mount; deregister on unmount. Single ref
  // shared between `<BuilderPage>` + this component — no React Context
  // because the registry is single-consumer.
  // ADR-0021 Option B (S7.3): bridge is reset-only. `applyDiff` has been
  // removed — small-gap catch-up now goes through invalidateQueries in the
  // channel hook. `reset()` is the large-gap/snapshot reseed path.
  useEffect(() => {
    if (!bridgeRef) return undefined;
    bridgeRef.current = {
      reset: () => {
        setCellValues(data.cellValues);
        setInputValues(data.inputValues);
      },
    };
    return () => {
      bridgeRef.current = {};
    };
    // Single-mount registration. `reset()` reads `data.*` from a closure
    // that is acceptably stale because the prop-driven
    // `useEffect [data.cellValues, data.inputValues]` re-seeds local state
    // from the latest `data` after any snapshot-applied refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bridgeRef]);

  // Phase 3 Session 3.3 (model-view-ux-polish, 2026-04-27): fullscreen toggle.
  // Local component state — resets on unmount (route navigation away from the
  // Model tab destroys this component, dropping the state). Parent gets
  // notified via `onFullscreenChange` so it can hide the conversation sidebar.
  // Per spec §6: BuilderTabBar + BuilderToolbar REMAIN visible — only the
  // sidebar + (in conversation tab) chat column hide.
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Phase 3 S3 — keyboard shortcuts help overlay (`?` key).
  const [shortcutsOverlayOpen, setShortcutsOverlayOpen] = useState(false);

  // F01 (post-ship review 2026-05-25, /review Pass 3): the parent-notify
  // call MUST sit outside the `setIsFullscreen` updater. Calling
  // `onFullscreenChange?(next)` inside the updater wrote to the parent's
  // `setIsModelFullscreen` from within the child's render-batch — React
  // surfaces "Cannot update a component while rendering a different
  // component". Lift the notify into a `useEffect` on the resolved value
  // so the parent setState queues for the next render cycle, not the
  // current one. Skip the first run (mount) so the contract stays
  // user-initiated-only — the existing test suite locks "spy called
  // once per user toggle, never on mount". Benign in production (React
  // strips warnings) but the post-ship #5 chrome gating made the warning
  // prominent in dev sessions.
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => !prev);
  }, []);
  const fullscreenDidMountRef = useRef(false);
  useEffect(() => {
    if (!fullscreenDidMountRef.current) {
      fullscreenDidMountRef.current = true;
      return;
    }
    onFullscreenChange?.(isFullscreen);
  }, [isFullscreen, onFullscreenChange]);

  // Keyboard handler — two responsibilities:
  //   1. F / Esc: fullscreen toggle (existing, Phase 3 Session 3.3).
  //   2. Ctrl+Shift+= / Ctrl+Shift+- / Ctrl+Shift+0: DIMENSIONAL zoom
  //      step/step-down/reset (Phase 2 S2, ADR-0012).
  //
  // The handler attaches to the root `<div>` (tabIndex={-1}) so it only
  // fires when focus is inside the model area. INPUT/TEXTAREA/contentEditable
  // bail prevents hijacking text-entry shortcuts.
  //
  // Zoom shortcut scope: ONLY the Shift-qualified variants (Ctrl+Shift+=/-/0)
  // preventDefault. Bare Ctrl+= and Ctrl+- (browser page zoom) are NOT
  // intercepted — the Shift qualifier disambiguates. Ctrl+Shift+0 is distinct
  // from Ctrl+0 (browser zoom-reset) for the same reason.
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      // INPUT/TEXTAREA/contentEditable guard — applies to ALL branches below.
      const target = e.target as HTMLElement;
      const inTextEntry =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      // --- Ctrl+F / Cmd+F (macOS): focus TreeNavigator search (ADR-0006) ---
      // Must be checked BEFORE the bare 'f' fullscreen branch so that
      // Ctrl+F / Cmd+F is intercepted here and never falls through to
      // the fullscreen toggle. The bare `F` branch below fires only
      // when neither ctrlKey nor metaKey is held.
      if ((e.ctrlKey || e.metaKey) && (e.key === 'f' || e.key === 'F')) {
        if (inTextEntry) return;
        e.preventDefault(); // override browser-native find dialog
        treeNavigatorRef.current?.focusSearchInput();
        return;
      }

      // ======================================================================
      // Phase 3 Session 2 — Ctrl+G / Cmd+G + Ctrl+Shift+G / Cmd+Shift+G.
      // Cycle through TreeNavigator's gridMatches results (cross-pane shortcut
      // — dispatches via treeNavigatorRef per ADR-0008 supersession
      // 2026-05-25). preventDefault on every fire to suppress Chrome's
      // built-in Find Next. Placement: AFTER Ctrl+F (semantic family — both
      // are TreeNavigator dispatches), BEFORE bare F (more-specific-before-
      // less-specific per Block B ordering invariant).
      // ======================================================================

      // --- Ctrl+Shift+G / Cmd+Shift+G — Find Previous (more-specific BEFORE
      // less-specific per Block B ordering invariant). ---
      if (
        (e.ctrlKey || e.metaKey) &&
        e.shiftKey &&
        (e.key === 'g' || e.key === 'G')
      ) {
        if (inTextEntry) return;
        e.preventDefault();
        treeNavigatorRef.current?.cycleToPrevMatch();
        return;
      }

      // --- Ctrl+G / Cmd+G — Find Next. ---
      if (
        (e.ctrlKey || e.metaKey) &&
        !e.shiftKey &&
        (e.key === 'g' || e.key === 'G')
      ) {
        if (inTextEntry) return;
        e.preventDefault();
        treeNavigatorRef.current?.cycleToNextMatch();
        return;
      }

      // --- Fullscreen: F key (case-insensitive, no modifier) ---
      if (e.key === 'f' || e.key === 'F') {
        if (inTextEntry) return;
        e.preventDefault();
        toggleFullscreen();
        return;
      }

      // --- Fullscreen: Escape exits (only when already fullscreen) ---
      if (e.key === 'Escape' && isFullscreen) {
        e.preventDefault();
        toggleFullscreen();
        return;
      }

      // --- Zoom: Ctrl+Shift+= (zoom in one step) ---
      if (e.ctrlKey && e.shiftKey && e.key === '=') {
        if (inTextEntry) return;
        e.preventDefault();
        setZoom((prev) => {
          const i = ZOOM_LEVELS.indexOf(prev);
          return i < ZOOM_LEVELS.length - 1 ? ZOOM_LEVELS[i + 1] : prev;
        });
        return;
      }

      // --- Zoom: Ctrl+Shift+- (zoom out one step) ---
      if (e.ctrlKey && e.shiftKey && e.key === '-') {
        if (inTextEntry) return;
        e.preventDefault();
        setZoom((prev) => {
          const i = ZOOM_LEVELS.indexOf(prev);
          return i > 0 ? ZOOM_LEVELS[i - 1] : prev;
        });
        return;
      }

      // --- Zoom: Ctrl+Shift+0 (reset to DEFAULT_ZOOM = 100) ---
      if (e.ctrlKey && e.shiftKey && e.key === '0') {
        if (inTextEntry) return;
        e.preventDefault();
        setZoom(DEFAULT_ZOOM);
        return;
      }

      // ======================================================================
      // Phase 3 Session 3 — `?` key opens keyboard shortcuts help overlay.
      // On US layout `?` is Shift+/; KeyboardEvent.key reports the SHIFTED
      // character directly so `e.key === '?'` resolves correctly (no
      // modifier-strip needed). INPUT/TEXTAREA bail via inTextEntry (above)
      // so user typing `?` in TreeNavigator's search box does NOT trigger
      // the overlay.
      // ======================================================================
      if (e.key === '?') {
        if (inTextEntry) return;
        e.preventDefault();
        setShortcutsOverlayOpen(true);
      }
    },
    [isFullscreen, toggleFullscreen]
  );

  // Phase 3 Session 3.4 (model-view-ux-polish, 2026-04-27): per-sheet
  // column-width overrides + persistence via the new PATCH /ui-prefs
  // endpoint. Seeded from `uiPrefs?.colWidths` (sourced from
  // `useConversation()` in the parent route). Drag-end on a `<ColHeaderCell>`
  // calls `handleColResize`, which (a) updates local state immediately for
  // visual continuity and (b) schedules an 800ms-debounced persist via
  // `useUiPrefs`. Reset clears the sheet's entry and persists immediately
  // (no debounce — explicit user intent).
  const [colWidthsBySheet, setColWidthsBySheet] = useState<
    Record<string, Record<string, number>>
  >(() => uiPrefs?.colWidths ?? {});

  const { mutate: persistUiPrefs } = useUiPrefs(conversationId);

  // ---------------------------------------------------------------------------
  // Phase 2 S2 — Zoom state owner (ADR-0012 single-source).
  //
  // Authenticated builder: seed from uiPrefs?.zoom (persisted per-conversation).
  // Public viewer (conversationId === null): seed from DEFAULT_ZOOM — ephemeral
  // React state only, never persisted (ADR-0003). useUiPrefs(null) already
  // short-circuits its mutate to a no-op at hook line 28, so no special-case
  // guard is needed here — the debounced mutate below is always safe to call.
  // ---------------------------------------------------------------------------
  const [zoom, setZoom] = useState<ZoomLevel>(uiPrefs?.zoom ?? DEFAULT_ZOOM);

  // Debounced zoom persist — 300ms to absorb slider drags + Ctrl+Shift+= bursts.
  // Separate ref from persistTimerRef (colWidths path) to avoid interference.
  const zoomPersistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  useEffect(() => {
    // Clear any previously scheduled persist.
    if (zoomPersistTimerRef.current) clearTimeout(zoomPersistTimerRef.current);
    zoomPersistTimerRef.current = setTimeout(() => {
      zoomPersistTimerRef.current = null;
      persistUiPrefs({ zoom });
    }, 300);
    return () => {
      if (zoomPersistTimerRef.current)
        clearTimeout(zoomPersistTimerRef.current);
    };
    // persistUiPrefs is stable (useMutation returns a stable mutate reference).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom]);

  // Hand-rolled debounce — lodash isn't a dep and we only need the one
  // call site. Ref-stored timer survives renders without re-creating;
  // each call resets the pending timer to fire 800ms after the latest
  // colWidthsBySheet change. The cleanup effect below clears it on
  // unmount so a stale timer can't fire after route change.
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    };
  }, []);
  const schedulePersist = useCallback(
    (next: Record<string, Record<string, number>>) => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
      persistTimerRef.current = setTimeout(() => {
        persistTimerRef.current = null;
        const patch: UiPrefsPatch = { colWidths: next };
        persistUiPrefs(patch);
      }, 800);
    },
    [persistUiPrefs]
  );

  const handleColResize = useCallback(
    (sheet: string, colIndex: number, widthPx: number) => {
      setColWidthsBySheet((prev) => {
        const next = {
          ...prev,
          [sheet]: { ...(prev[sheet] ?? {}), [String(colIndex)]: widthPx },
        };
        schedulePersist(next);
        return next;
      });
    },
    [schedulePersist]
  );

  const handleResetSheetWidths = useCallback(
    (sheet: string) => {
      setColWidthsBySheet((prev) => {
        const next = { ...prev };
        delete next[sheet];
        // Reset is explicit user intent — fire immediately, no debounce.
        // Cancel any pending debounced persist so it can't overwrite the
        // reset with the pre-reset state.
        if (persistTimerRef.current) {
          clearTimeout(persistTimerRef.current);
          persistTimerRef.current = null;
        }
        const patch: UiPrefsPatch = { colWidths: next };
        persistUiPrefs(patch);
        return next;
      });
    },
    [persistUiPrefs]
  );

  // Set of sheets with at least one width override — drives the per-tab
  // reset icon's conditional render. Recomputed when the map changes.
  const sheetsWithOverrides = useMemo(
    () =>
      new Set(
        Object.entries(colWidthsBySheet)
          .filter(([, widths]) => Object.keys(widths).length > 0)
          .map(([sheet]) => sheet)
      ),
    [colWidthsBySheet]
  );

  // SessionStorage-backed collapsed state — survives Conversation/Model tab
  // switches but resets across browser sessions (per spec §6 + Phase 4 S1
  // blueprint). Initial read happens in `useState` callback so SSR-style
  // prerenders (none today, but defensively) don't crash on `window`.
  const [treeCollapsed, setTreeCollapsed] = useState<boolean>(() =>
    readCollapsedFromStorage()
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.sessionStorage.setItem(
        TREE_COLLAPSED_KEY,
        treeCollapsed ? '1' : '0'
      );
    } catch {
      // Quota exhaustion / private mode — silent fallback (state still works
      // in-memory for this session).
    }
  }, [treeCollapsed]);

  const sheet = useMemo(
    () =>
      data.layout.sheets.find((s) => s.name === activeSheet) ??
      data.layout.sheets[0],
    [data.layout.sheets, activeSheet]
  );

  // Tree-navigator's selectedCell shape includes `sheet` so the active-leaf
  // highlight only matches a leaf when its target's sheet matches the
  // currently-selected cell's sheet. Without `sheet`, two cells at the same
  // (row, col) on different sheets would both light up.
  const treeSelectedCell = useMemo(
    () =>
      selectedCell
        ? { ...selectedCell, sheet: sheet?.name ?? activeSheet }
        : null,
    [selectedCell, sheet?.name, activeSheet]
  );

  // All hooks MUST be called before any conditional return (Rules of Hooks).
  // `selectAndPulse`, `handleUpstreamSelect`, and the deep-link `useEffect`
  // are placed here — before the `if (!sheet)` guard — so the hook call
  // order is unconditional on every render.

  const selectAndPulse = useCallback(
    (target: { sheet: string; row: number; col: number }, leafId?: string) => {
      if (target.sheet !== activeSheet) {
        setActiveSheet(target.sheet);
      }
      setSelectedCell({ row: target.row, col: target.col });
      // Tree-leaf clicks carry a unique leafId → highlight that leaf only.
      // Cell-/search-/deep-link-originated calls pass no leafId → null → the
      // Leaf falls back to cell match. (tree-nav multi-select fix, 2026-06-11.)
      setSelectedLeafId(leafId ?? null);
      nonceRef.current += 1;
      setScrollToCell({
        row: target.row,
        col: target.col,
        nonce: nonceRef.current,
      });
    },
    [activeSheet]
  );

  const handleUpstreamSelect = useCallback(
    (namedRange: string) => {
      const nr = findNamedRange(namedRange, data.schema);
      if (!nr || !nr.sheet || !nr.cell) {
        // Defensive: schema-side gap (rare). Surface a warning so the
        // implementer can spot it; the click is otherwise a no-op.
        // eslint-disable-next-line no-console
        console.warn(
          `[CellDetailPanel] upstream namedRange "${namedRange}" missing sheet/cell — click is a no-op.`
        );
        return;
      }
      const parsed = parseA1(nr.cell);
      if (!parsed) return;
      selectAndPulse({ sheet: nr.sheet, row: parsed.row, col: parsed.col });
    },
    [data.schema, selectAndPulse]
  );

  // Phase 2 S4 — "Show provenance" context-menu action: select the target cell
  // (which drives CellDetailPanel to re-render for that cell). The panel always
  // shows provenance when available for the selected input cell. No separate
  // tab-switch is needed — CellDetailPanel auto-routes on cellType.
  const handleShowProvenance = useCallback(
    (target: { sheet: string; row: number; col: number }) => {
      selectAndPulse(target);
    },
    [selectAndPulse]
  );

  // Phase 2 S4 — "View in conversation" context-menu action: dispatch a window
  // event consumed by ConversationPane. Also calls the parent's
  // `onViewInConversation` (flips ?tab=conversation) when available. Only wired
  // when conversationId is non-null (public-viewer gate in ModelGrid already
  // ensures this is never called with a null conversationId).
  const handleViewInConversationFromCell = useCallback(
    (_target: { sheet: string; row: number; col: number }) => {
      window.dispatchEvent(
        new CustomEvent('arcanum:view-in-conversation', { detail: _target })
      );
      // Also flip the parent tab if the callback is wired (auth-mode builder).
      onViewInConversation?.();
    },
    [onViewInConversation]
  );

  // Phase 3 Session 5: deep-link resolution. Watches `cellParam` +
  // `namedRangeParam`; on transition from undefined → defined, resolves
  // through `resolveDeepLink` and dispatches `selectAndPulse`. Successful
  // resolution invokes `onConsumeDeepLink` so the parent drops the URL
  // param (prevents re-trigger on subsequent prop changes). Failure path
  // logs via `logResolutionFailure` (silent — never an error modal).
  useEffect(() => {
    if (!cellParam && !namedRangeParam) return;
    if (cellParam) {
      const r = resolveDeepLink(
        { surface: 'model', cell: cellParam },
        data.schema as ModelSchemaForDeepLink
      );
      if (r.ok && r.target.kind === 'cell') {
        selectAndPulse({
          sheet: r.target.sheet,
          row: r.target.row,
          col: r.target.col,
        });
        onConsumeDeepLink?.({ cell: true });
      } else if (!r.ok) {
        logResolutionFailure('model', r);
        onConsumeDeepLink?.({ cell: true });
      }
    }
    if (namedRangeParam) {
      const r = resolveDeepLink(
        { surface: 'model', namedRange: namedRangeParam },
        data.schema as ModelSchemaForDeepLink
      );
      if (r.ok && r.target.kind === 'namedRange') {
        selectAndPulse({
          sheet: r.target.sheet,
          row: r.target.row,
          col: r.target.col,
        });
        onConsumeDeepLink?.({ namedRange: true });
      } else if (!r.ok) {
        logResolutionFailure('model', r);
        onConsumeDeepLink?.({ namedRange: true });
      }
    }
    // selectAndPulse + onConsumeDeepLink are referentially stable per
    // useCallback / parent's stable handler — re-run only on URL changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cellParam, namedRangeParam, data.schema]);

  if (!sheet) {
    // Defensive: a layout with zero sheets shouldn't reach here (the
    // `layoutMissing: true` branch is the canonical empty path), but guard
    // against an empty `sheets[]` array regardless.
    return (
      <div className="flex-1 flex items-center justify-center text-sm font-mono text-text-muted">
        No sheets to display.
      </div>
    );
  }

  return (
    <div
      className="flex h-full min-h-0"
      onKeyDown={handleKeyDown}
      tabIndex={-1}
      data-testid="model-layout-root"
      data-fullscreen={isFullscreen ? 'true' : 'false'}
      data-read-only={readOnly ? 'true' : 'false'}
    >
      <TreeNavigator
        ref={treeNavigatorRef}
        schema={data.schema}
        visibleSheetNames={data.layout.visibleSheetNames}
        layout={data.layout}
        cellValues={cellValues}
        selectedCell={treeSelectedCell}
        selectedLeafId={selectedLeafId}
        onLeafSelect={selectAndPulse}
        collapsed={treeCollapsed}
        onToggleCollapse={() => setTreeCollapsed((prev) => !prev)}
        searchParam={searchParam}
        onSearchChange={onSearchChange}
      />
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top toolbar — fullscreen toggle (right-aligned). Thin 28px bar
            mirrors the grid's COL_HEADER_HEIGHT for visual continuity.
            Phase 3 Session 3.3 (model-view-ux-polish, 2026-04-27). */}
        <div
          className="flex items-center justify-end h-7 px-2 border-b border-panel-border bg-panel-bg shrink-0"
          data-testid="model-layout-toolbar"
        >
          <button
            type="button"
            onClick={toggleFullscreen}
            className="flex items-center justify-center w-6 h-6 text-text-muted hover:text-text-bright transition-colors cursor-pointer bg-transparent border-none"
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            title={
              isFullscreen ? 'Exit fullscreen (Esc)' : 'Enter fullscreen (F)'
            }
            data-testid="model-fullscreen-toggle"
            data-state={isFullscreen ? 'fullscreen' : 'windowed'}
          >
            {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>
        </div>
        <ModelGrid
          layout={sheet}
          cellValues={cellValues}
          inputValues={inputValues}
          schema={data.schema}
          selectedCell={selectedCell}
          onCellSelect={(row, col) => {
            setSelectedCell({ row, col });
            // Grid-originated selection has no tree leaf identity → clear it so
            // the Leaf falls back to cell match (and never keeps a stale
            // tree-leaf highlight). (tree-nav multi-select fix, 2026-06-11.)
            setSelectedLeafId(null);
          }}
          onClearSelection={() => {
            setSelectedCell(null);
            setSelectedLeafId(null);
          }}
          selectedCellRef={
            selectedCellRef as RefObject<{ row: number; col: number } | null>
          }
          scrollToCell={scrollToCell}
          readOnly={readOnly}
          colWidths={colWidthsBySheet[sheet.name]}
          onColWidthChange={(colIndex, widthPx) =>
            handleColResize(sheet.name, colIndex, widthPx)
          }
          zoom={zoom}
          onZoomChange={setZoom}
          conversationId={conversationId}
          onReveal={selectAndPulse}
          onShowProvenance={handleShowProvenance}
          onViewInConversation={
            conversationId !== null
              ? handleViewInConversationFromCell
              : undefined
          }
        />
        <SheetTabs
          sheets={data.layout.visibleSheetNames}
          activeSheet={activeSheet}
          onSheetSelect={(s) => {
            setActiveSheet(s);
            setSelectedCell(null);
          }}
          sheetsWithOverrides={sheetsWithOverrides}
          onResetSheetWidths={handleResetSheetWidths}
          zoom={zoom}
          onZoomChange={setZoom}
        />
      </div>
      <CellDetailPanel
        selectedCell={selectedCell}
        sheet={sheet}
        schema={data.schema}
        cellValues={cellValues}
        inputValues={inputValues}
        provenance={data.provenance}
        onUpstreamSelect={handleUpstreamSelect}
        conversationId={conversationId}
        onViewInConversation={onViewInConversation}
        onInputEdit={onInputEdit}
        readOnly={readOnly}
      />
      {/* Phase 3 S3 — keyboard shortcuts help overlay (`?` key). */}
      <KeyboardShortcutsOverlay
        isOpen={shortcutsOverlayOpen}
        onClose={() => setShortcutsOverlayOpen(false)}
      />
    </div>
  );
}
